import React, { useEffect, useMemo, useRef, useState } from "react";

import QuickFilterButton from "../components/common/QuickFilterButton";
import SummaryCard from "../components/common/SummaryCard";

import EventTable from "../components/events/EventTable";
import EventMobileList from "../components/events/EventMobileList";
import EventPagination from "../components/events/EventPagination";
import ViolationClipModal from "../components/events/ViolationClipModal";

import {
  extractEventRows,
  isWatchEvent,
  normalizeEventRows,
} from "../utils/eventUtils";

const EVENT_POLL_MS = 30000;
const EVENT_HIDDEN_POLL_MS = 60000;
const PAGE_SIZE = 8;

function isAdminUser(me) {
  const role = String(me?.roleCode || "").toUpperCase();
  return role === "SUPER_ADMIN" || role === "ADMIN";
}

function isMintcamEvent(row) {
  const eventType = String(row?.eventType || "").toUpperCase();
  const type = String(row?.type || "").trim();
  const message = String(row?.message || "").trim();
  const source = String(row?.eventSource || row?.source || "").trim();
  const note = String(
    row?.note || row?.remark || row?.eventReason || row?.eventReasonLabel || ""
  ).trim();

  return (
    eventType === "MINTCAM_ON" ||
    eventType === "MINTCAM_OFF" ||
    eventType === "PPE_VIOLATION" ||
    row?.isViolation === true ||
    type.includes("미착용") ||
    type.includes("민트캠") ||
    message.includes("영상 송출") ||
    message.includes("상태 수신") ||
    source.toUpperCase().includes("RTMP") ||
    source.toUpperCase().includes("APP") ||
    note.toUpperCase().includes("RTMP") ||
    note.toUpperCase().includes("APP") ||
    note.includes("송출 서버")
  );
}

function getMintcamEventDirection(row) {
  const eventType = String(row?.eventType || "").toUpperCase();
  const type = String(row?.type || "").trim();
  const message = String(row?.message || "").trim();
  const note = String(
    row?.note || row?.remark || row?.eventReason || row?.eventReasonLabel || ""
  ).trim();

  if (
    eventType === "MINTCAM_ON" ||
    type.includes("켜짐") ||
    message.includes("시작") ||
    message.includes("켜짐") ||
    note.includes("연결성공") ||
    note.includes("정상 연결")
  ) {
    return "ON";
  }

  if (
    eventType === "MINTCAM_OFF" ||
    type.includes("꺼짐") ||
    message.includes("중단") ||
    message.includes("꺼짐") ||
    message.includes("종료") ||
    note.includes("앱 종료") ||
    note.includes("상태 수신 없음") ||
    note.includes("연결 종료")
  ) {
    return "OFF";
  }

  return "";
}

function countByEventType(logs, type) {
  const wanted = String(type || "").toUpperCase();

  return logs.filter(
    (row) => String(row?.eventType || "").toUpperCase() === wanted
  ).length;
}

function countMintcamEvents(logs) {
  // 민트캠 = 카메라 켜짐/꺼짐 등 상태 이벤트만 (PPE는 안전모/안전조끼로 분리)
  return logs.filter((row) => isMintcamEvent(row) && !isPpeViolation(row)).length;
}

function countWatchEvents(logs) {
  return logs.filter((row) => isWatchEvent(row)).length;
}

function buildMyCameraMap(cameras) {
  const map = new Map();

  (Array.isArray(cameras) ? cameras : []).forEach((cam, idx) => {
    const name =
      cam?.name ||
      cam?.camName ||
      cam?.camTitle ||
      cam?.title ||
      `CAM ${cam?.camNo || idx + 1}`;

    const streamKey = String(cam?.streamKey || "").trim();
    const camIdx = String(cam?.camIdx || cam?.id || "").trim();
    const camNo = String(cam?.camNo || "").trim();

    const candidates = [
      streamKey,
      streamKey ? `live/${streamKey}` : "",
      streamKey ? `/live/${streamKey}` : "",
      camIdx,
      camNo,
      String(cam?.deviceId || "").trim(),
      String(cam?.path || "").trim(),
      String(cam?.streamPath || "").trim(),
      String(cam?.name || "").trim(),
      String(cam?.camName || "").trim(),
      String(cam?.camTitle || "").trim(),
    ].filter(Boolean);

    candidates.forEach((key) => {
      map.set(key, name);
    });
  });

  return map;
}

function resolveDeviceName(row, myCameraMap) {
  const candidates = [
    String(row?.deviceName || "").trim(),
    String(row?.deviceId || "").trim(),
    String(row?.streamKey || "").trim(),
    String(row?.streamPath || "").trim(),
    String(row?.path || "").trim(),
    String(row?.cameraId || "").trim(),
    String(row?.camIdx || "").trim(),
    String(row?.camNo || "").trim(),
  ].filter(Boolean);

  for (const key of candidates) {
    if (myCameraMap.has(key)) {
      return myCameraMap.get(key);
    }
  }

  const rawName = String(row?.deviceName || "").trim();

  if (rawName.startsWith("live/") || rawName.startsWith("/live/")) {
    const streamKey = rawName.replace(/^\/?live\//, "");
    if (myCameraMap.has(streamKey)) {
      return myCameraMap.get(streamKey);
    }
  }

  return rawName || "-";
}

function belongsToMyCamera(row, myCameraMap) {
  if (!myCameraMap || myCameraMap.size === 0) return true;

  const candidates = [
    String(row?.deviceName || "").trim(),
    String(row?.deviceId || "").trim(),
    String(row?.streamKey || "").trim(),
    String(row?.streamPath || "").trim(),
    String(row?.path || "").trim(),
    String(row?.cameraId || "").trim(),
    String(row?.camIdx || "").trim(),
    String(row?.camNo || "").trim(),
  ].filter(Boolean);

  for (const key of candidates) {
    if (myCameraMap.has(key)) return true;

    if (key.startsWith("live/") || key.startsWith("/live/")) {
      const streamKey = key.replace(/^\/?live\//, "");
      if (myCameraMap.has(streamKey)) return true;
    }
  }

  return false;
}

function normalizeUserMintcamRow(row, myCameraMap) {
  const direction = getMintcamEventDirection(row);
  const deviceName = resolveDeviceName(row, myCameraMap);

  const type =
    direction === "ON"
      ? "민트캠 켜짐"
      : direction === "OFF"
      ? "민트캠 꺼짐"
      : row?.type || "민트캠";

  const message =
    row?.message ||
    (direction === "ON"
      ? `${deviceName} 영상 송출이 시작되었습니다.`
      : direction === "OFF"
      ? `${deviceName} 상태 수신이 중단되었습니다.`
      : `${deviceName} 민트캠 이벤트가 발생했습니다.`);

  return {
    ...row,
    type,
    deviceName,
    message,
  };
}

// AI 안전장구(PPE) 위반 종류 판별
function isPpeViolation(row) {
  return String(row?.eventType || "").toUpperCase() === "PPE_VIOLATION";
}
function isHelmetViolation(row) {
  if (!isPpeViolation(row)) return false;
  const r = String(row?.eventReason || row?.rawEventReason || "").toUpperCase();
  return r === "NO_HELMET" || String(row?.type || "").includes("안전모");
}
function isVestViolation(row) {
  if (!isPpeViolation(row)) return false;
  const r = String(row?.eventReason || row?.rawEventReason || "").toUpperCase();
  return (
    r === "NO_VEST" ||
    String(row?.type || "").includes("안전조끼") ||
    String(row?.type || "").includes("조끼")
  );
}
function isHarnessViolation(row) {
  if (!isPpeViolation(row)) return false;
  const r = String(row?.eventReason || row?.rawEventReason || "").toUpperCase();
  return (
    r === "NO_HARNESS" ||
    String(row?.type || "").includes("안전대") ||
    String(row?.type || "").includes("하네스")
  );
}
function countHelmet(logs) {
  return logs.filter(isHelmetViolation).length;
}
function countVest(logs) {
  return logs.filter(isVestViolation).length;
}
function countHarness(logs) {
  return logs.filter(isHarnessViolation).length;
}

function filterLogsByQuickFilter(logs, quickFilter, canSeeAllEvents) {
  const filter = String(quickFilter || "ALL").toUpperCase();

  if (filter === "ALL") return logs;

  if (filter === "MINTCAM") {
    // 민트캠 = 카메라 켜짐/꺼짐 등 상태만. PPE(안전모/조끼)는 별도 필터.
    return logs.filter((row) => isMintcamEvent(row) && !isPpeViolation(row));
  }

  if (filter === "MINTCAM_ON") {
    return logs.filter((row) => getMintcamEventDirection(row) === "ON");
  }

  if (filter === "MINTCAM_OFF") {
    return logs.filter((row) => getMintcamEventDirection(row) === "OFF");
  }

  // 안전모/안전조끼 — PPE 라이선스 사용자(관리자 아니어도) 사용 가능
  if (filter === "HELMET") {
    return logs.filter((row) => isHelmetViolation(row));
  }
  if (filter === "VEST") {
    return logs.filter((row) => isVestViolation(row));
  }
  if (filter === "HARNESS") {
    return logs.filter((row) => isHarnessViolation(row));
  }

  if (!canSeeAllEvents) {
    return logs;
  }

  if (filter === "WATCH") {
    return logs.filter((row) => isWatchEvent(row));
  }

  if (filter === "SOS") {
    return logs.filter((row) => {
      const eventType = String(row?.eventType || "").toUpperCase();
      return eventType === "SOS" || eventType === "SOS_DISMISS";
    });
  }

  if (filter === "HEART_RATE") {
    return logs.filter((row) => {
      const eventType = String(row?.eventType || "").toUpperCase();
      return (
        eventType === "HEART_RATE_HIGH" ||
        eventType === "HEART_RATE_HIGH_DISMISS"
      );
    });
  }

  return logs;
}

function filterLogsBySearch(logs, search) {
  const q = String(search || "").trim().toLowerCase();

  if (!q) return logs;

  return logs.filter((row) => {
    const haystack = [
      row?.type,
      row?.eventType,
      row?.deviceId,
      row?.deviceName,
      // 작업자 이름으로 검색 — 이름을 치면 그 사람 이력만 남는다.
      // (카메라 1대는 한 계정에 고정돼 있어 소유자가 곧 사용자다)
      row?.workerName,
      row?.message,
      row?.eventReason,
      row?.eventReasonLabel,
      row?.eventSource,
      row?.eventSourceLabel,
      row?.triggerType,
      row?.source,
      row?.dismissedBy,
      row?.dismissReason,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    return haystack.includes(q);
  });
}

function getPageNumbers(currentPage, totalPages) {
  const pages = [];
  const start = Math.max(1, currentPage - 2);
  const end = Math.min(totalPages, currentPage + 2);

  for (let page = start; page <= end; page += 1) {
    pages.push(page);
  }

  return pages;
}

function getTodayDateValue() {
  const now = new Date();

  return new Intl.DateTimeFormat("sv-SE", {
    timeZone: "Asia/Seoul",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(now);
}

function toDateKey(date) {
  return new Intl.DateTimeFormat("sv-SE", {
    timeZone: "Asia/Seoul",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
}

function formatDateDisplay(value) {
  if (!value) return "-";

  const parts = String(value).split("-");

  if (parts.length !== 3) return value;

  const year = parts[0];
  const month = String(Number(parts[1]));
  const day = String(Number(parts[2]));

  return `${year}. ${month}. ${day}.`;
}

function getRowDateValue(row) {
  const raw = String(
    row?.createdAt ||
      row?.CreatedAt ||
      row?.timestamp ||
      row?.Timestamp ||
      row?.displayTime ||
      row?.eventTime ||
      row?.EventTime ||
      row?.regDate ||
      row?.RegDate ||
      ""
  ).trim();

  if (!raw) return "";

  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
    return raw.slice(0, 10);
  }

  const date = new Date(raw);

  if (Number.isNaN(date.getTime())) return "";

  return new Intl.DateTimeFormat("sv-SE", {
    timeZone: "Asia/Seoul",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
}

function EventCalendarInput({ value, onChange, markedDateSet }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const selectedDate = value ? new Date(`${value}T00:00:00`) : new Date();

  const [viewYear, setViewYear] = useState(selectedDate.getFullYear());
  const [viewMonth, setViewMonth] = useState(selectedDate.getMonth());

  useEffect(() => {
    function handleClickOutside(e) {
      if (!wrapRef.current) return;

      if (!wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!value) return;

    const d = new Date(`${value}T00:00:00`);

    if (Number.isNaN(d.getTime())) return;

    setViewYear(d.getFullYear());
    setViewMonth(d.getMonth());
  }, [value]);

  const moveMonth = (diff) => {
    const d = new Date(viewYear, viewMonth + diff, 1);
    setViewYear(d.getFullYear());
    setViewMonth(d.getMonth());
  };

  const todayKey = getTodayDateValue();
  const selectedKey = value;

  const firstDay = new Date(viewYear, viewMonth, 1);
  const startDay = firstDay.getDay();
  const startDate = new Date(viewYear, viewMonth, 1 - startDay);

  const days = Array.from({ length: 42 }, (_, i) => {
    const d = new Date(startDate);
    d.setDate(startDate.getDate() + i);
    return d;
  });

  const selectDate = (date) => {
    const nextValue = toDateKey(date);

    onChange({
      target: {
        value: nextValue,
      },
    });

    setOpen(false);
  };

  const selectedLabel = formatDateDisplay(value);

  return (
    <div
      ref={wrapRef}
      className="relative h-12 w-full min-w-0 max-w-full rounded-2xl sm:w-[180px]"
    >
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="relative flex h-full w-full items-center justify-center rounded-2xl border border-gray-200 bg-white px-4 pr-12 text-center text-[16px] font-medium leading-none text-gray-900 outline-none transition focus:border-blue-400 focus:ring-2 focus:ring-blue-400/20 dark:border-white/10 dark:bg-[#0F172A] dark:text-white sm:text-sm"
      >
        <span className="flex h-full min-w-0 items-center justify-center truncate leading-none">
          {selectedLabel}
        </span>

        <span className="pointer-events-none absolute right-4 top-1/2 flex -translate-y-1/2 items-center justify-center text-gray-500 dark:text-gray-300">
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M7 3v3M17 3v3M4 9h16M6 5h12a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </button>

      {open && (
        <div className="absolute left-0 top-[54px] z-50 w-[280px] rounded-2xl border border-gray-200 bg-white p-3 shadow-xl dark:border-white/10 dark:bg-[#111827]">
          <div className="mb-3 flex items-center justify-between">
            <button
              type="button"
              onClick={() => moveMonth(-1)}
              className="flex h-8 w-8 items-center justify-center rounded-lg bg-gray-100 text-lg font-bold text-gray-700 hover:bg-gray-200 dark:bg-white/10 dark:text-gray-200 dark:hover:bg-white/20"
              aria-label="이전 달"
            >
              ‹
            </button>

            <div className="text-sm font-bold text-gray-900 dark:text-white">
              {viewYear}. {viewMonth + 1}.
            </div>

            <button
              type="button"
              onClick={() => moveMonth(1)}
              className="flex h-8 w-8 items-center justify-center rounded-lg bg-gray-100 text-lg font-bold text-gray-700 hover:bg-gray-200 dark:bg-white/10 dark:text-gray-200 dark:hover:bg-white/20"
              aria-label="다음 달"
            >
              ›
            </button>
          </div>

          <div className="mb-2 grid grid-cols-7 text-center text-[11px] font-semibold text-gray-500 dark:text-gray-400">
            <div>일</div>
            <div>월</div>
            <div>화</div>
            <div>수</div>
            <div>목</div>
            <div>금</div>
            <div>토</div>
          </div>

          <div className="grid grid-cols-7 gap-1">
            {days.map((date) => {
              const key = toDateKey(date);
              const inThisMonth = date.getMonth() === viewMonth;
              const selected = key === selectedKey;
              const today = key === todayKey;
              const hasEvent = markedDateSet?.has(key);

              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => selectDate(date)}
                  className={`relative flex h-8 items-center justify-center rounded-lg text-xs font-medium transition-colors ${
                    selected
                      ? "bg-blue-600 text-white shadow-sm"
                      : hasEvent
                      ? "bg-blue-100 text-blue-700 hover:bg-blue-200 dark:bg-blue-500/20 dark:text-blue-300 dark:hover:bg-blue-500/30"
                      : today
                      ? "border border-blue-400 text-gray-900 hover:bg-gray-100 dark:text-white dark:hover:bg-white/10"
                      : inThisMonth
                      ? "text-gray-800 hover:bg-gray-100 dark:text-gray-100 dark:hover:bg-white/10"
                      : "text-gray-300 hover:bg-gray-50 dark:text-gray-600 dark:hover:bg-white/5"
                  }`}
                >
                  {date.getDate()}

                  {hasEvent && !selected && (
                    <span className="absolute bottom-[3px] left-1/2 h-1 w-1 -translate-x-1/2 rounded-full bg-blue-600 dark:bg-blue-300" />
                  )}
                </button>
              );
            })}
          </div>

          <div className="mt-3 flex items-center justify-between text-[11px] text-gray-500 dark:text-gray-400">
            <div>파란색 표시 = 이벤트 있음</div>

            <button
              type="button"
              onClick={() => {
                onChange({
                  target: {
                    value: getTodayDateValue(),
                  },
                });

                setOpen(false);
              }}
              className="font-semibold text-blue-600 dark:text-blue-300"
            >
              오늘
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function csvEscape(value) {
  const text = String(value ?? "").replace(/\r?\n/g, " ").trim();

  if (
    text.includes(",") ||
    text.includes('"') ||
    text.includes("\n") ||
    text.includes("\r")
  ) {
    return `"${text.replace(/"/g, '""')}"`;
  }

  return text;
}

function getCsvTime(row) {
  return String(
    row?.displayTime || row?.createdAt || row?.timestamp || ""
  ).trim();
}

function getCsvRemark(row) {
  const left = String(
    row?.eventReasonLabel || row?.eventReason || row?.dismissReason || ""
  ).trim();

  const right = String(
    row?.eventSourceLabel || row?.eventSource || row?.source || ""
  ).trim();

  if (left && right) return `${left} / ${right}`;
  return left || right || "";
}

function downloadCsv(filename, rows) {
  const headers = [
    "시간",
    "타입",
    "이벤트코드",
    "장비ID",
    "장비명",
    "내용",
    "비고",
    "원본소스",
  ];

  const body = rows.map((row) => [
    getCsvTime(row),
    row?.type || "",
    row?.eventType || "",
    row?.deviceId || "",
    row?.deviceName || "",
    row?.message || "",
    getCsvRemark(row),
    row?.source || "",
  ]);

  const csv = [headers, ...body]
    .map((line) => line.map(csvEscape).join(","))
    .join("\r\n");

  const blob = new Blob(["\uFEFF" + csv], {
    type: "text/csv;charset=utf-8;",
  });

  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");

  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();

  URL.revokeObjectURL(url);
}

export default function EventsPage({ me, permissionFlags, cameras = [], licenseFeatures = {} }) {
  const canSeeAllEvents = isAdminUser(me);
  // 민트워치(유료 옵션)가 라이선스에 미포함이면 관리자라도 민트워치/SOS/심박수 UI를 숨긴다.
  // (값이 없으면 구버전 호환상 true로 본다.)
  const mintwatchEnabled = licenseFeatures?.mintwatchEnabled !== false;
  // 계정별 민트워치 자격(슈퍼어드민 or CanMessages)도 있어야 민트워치/SOS/심박 노출.
  const accountMintwatch = !!permissionFlags?.canMessages;
  const showWatch = canSeeAllEvents && mintwatchEnabled && accountMintwatch;
  // PPE(안전모·조끼)는 라이선스 발급된 경우에만 카드/필터 노출
  const ppeLicensed = licenseFeatures?.ppeEnabled === true;

  const [rawLogs, setRawLogs] = useState([]);
  const [markLogs, setMarkLogs] = useState([]);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  const [selectedDate, setSelectedDate] = useState(getTodayDateValue);
  const [search, setSearch] = useState("");
  const [quickFilter, setQuickFilter] = useState("ALL");
  const [currentPage, setCurrentPage] = useState(1);
  const [clipEvent, setClipEvent] = useState(null);

  const myCameraMap = useMemo(() => buildMyCameraMap(cameras), [cameras]);

  const fetchEvents = async ({ silent = false } = {}) => {
    try {
      if (silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      const query = selectedDate
        ? `?date=${encodeURIComponent(selectedDate)}`
        : "";

      const res = await fetch(`/api/event-log${query}`, {
        credentials: "include",
        cache: "no-store",
      });

      const data = await res.json().catch(() => []);

      if (res.ok) {
        const rows = extractEventRows(data);
        setRawLogs(normalizeEventRows(rows));
      } else {
        setRawLogs([]);
      }

      setLastUpdated(new Date());
    } catch (err) {
      console.error("EventsPage fetchEvents error:", err);
      setRawLogs([]);
      setLastUpdated(new Date());
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const fetchEventMarks = async () => {
    try {
      const res = await fetch("/api/event-log", {
        credentials: "include",
        cache: "no-store",
      });

      const data = await res.json().catch(() => []);

      if (res.ok) {
        const rows = extractEventRows(data);
        setMarkLogs(normalizeEventRows(rows));
      } else {
        setMarkLogs([]);
      }
    } catch (err) {
      console.error("EventsPage fetchEventMarks error:", err);
      setMarkLogs([]);
    }
  };

  useEffect(() => {
    let cancelled = false;
    let timer = null;

    const getNextDelay = () => {
      if (typeof document !== "undefined" && document.hidden) {
        return EVENT_HIDDEN_POLL_MS;
      }

      return EVENT_POLL_MS;
    };

    const run = async ({ silent = false } = {}) => {
      if (cancelled) return;

      await fetchEvents({ silent });
      await fetchEventMarks();

      if (cancelled) return;

      timer = setTimeout(() => {
        run({ silent: true });
      }, getNextDelay());
    };

    const handleVisibilityChange = () => {
      if (cancelled) return;

      if (timer) clearTimeout(timer);

      timer = setTimeout(() => {
        run({ silent: true });
      }, 500);
    };

    run({ silent: false });

    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", handleVisibilityChange);
    }

    return () => {
      cancelled = true;

      if (timer) clearTimeout(timer);

      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", handleVisibilityChange);
      }
    };
  }, [selectedDate]);

  useEffect(() => {
    fetchEventMarks();
  }, [canSeeAllEvents, myCameraMap]);

  const visibleBaseLogs = useMemo(() => {
    const dateFilteredLogs = selectedDate
      ? rawLogs.filter((row) => getRowDateValue(row) === selectedDate)
      : rawLogs;

    if (canSeeAllEvents) {
      // 민트워치 자격이 없으면 워치/SOS/심박 이벤트를 목록에서 제외한다.
      return showWatch
        ? dateFilteredLogs
        : dateFilteredLogs.filter((row) => !isWatchEvent(row));
    }

    return dateFilteredLogs
      .filter((row) => isMintcamEvent(row))
      .filter((row) => belongsToMyCamera(row, myCameraMap))
      .map((row) => normalizeUserMintcamRow(row, myCameraMap));
  }, [rawLogs, selectedDate, canSeeAllEvents, myCameraMap, showWatch]);

  const visibleMarkLogs = useMemo(() => {
    if (canSeeAllEvents) return markLogs;

    return markLogs
      .filter((row) => isMintcamEvent(row))
      .filter((row) => belongsToMyCamera(row, myCameraMap))
      .map((row) => normalizeUserMintcamRow(row, myCameraMap));
  }, [markLogs, canSeeAllEvents, myCameraMap]);

  const eventDateSet = useMemo(() => {
    const set = new Set();

    visibleMarkLogs.forEach((row) => {
      const key = getRowDateValue(row);

      if (key) {
        set.add(key);
      }
    });

    visibleBaseLogs.forEach((row) => {
      const key = getRowDateValue(row);

      if (key) {
        set.add(key);
      }
    });

    return set;
  }, [visibleMarkLogs, visibleBaseLogs]);

  const summary = useMemo(() => {
    return {
      total: visibleBaseLogs.length,
      watch: showWatch ? countWatchEvents(visibleBaseLogs) : 0,
      mintcam: countMintcamEvents(visibleBaseLogs),
      sos: showWatch ? countByEventType(visibleBaseLogs, "SOS") : 0,
      heartRate: showWatch
        ? countByEventType(visibleBaseLogs, "HEART_RATE_HIGH")
        : 0,
      mintcamOn: visibleBaseLogs.filter(
        (row) => getMintcamEventDirection(row) === "ON"
      ).length,
      mintcamOff: visibleBaseLogs.filter(
        (row) => getMintcamEventDirection(row) === "OFF"
      ).length,
      helmet: ppeLicensed ? countHelmet(visibleBaseLogs) : 0,
      vest: ppeLicensed ? countVest(visibleBaseLogs) : 0,
      harness: ppeLicensed ? countHarness(visibleBaseLogs) : 0,
    };
  }, [visibleBaseLogs, canSeeAllEvents, showWatch, ppeLicensed]);

  const filteredLogs = useMemo(() => {
    const quickFiltered = filterLogsByQuickFilter(
      visibleBaseLogs,
      quickFilter,
      canSeeAllEvents
    );

    return filterLogsBySearch(quickFiltered, search);
  }, [visibleBaseLogs, quickFilter, search, canSeeAllEvents]);

  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(filteredLogs.length / PAGE_SIZE));
  }, [filteredLogs.length]);

  const safeCurrentPage = Math.min(currentPage, totalPages);

  const pagedLogs = useMemo(() => {
    const start = (safeCurrentPage - 1) * PAGE_SIZE;
    return filteredLogs.slice(start, start + PAGE_SIZE);
  }, [filteredLogs, safeCurrentPage]);

  const pageNumbers = useMemo(() => {
    return getPageNumbers(safeCurrentPage, totalPages);
  }, [safeCurrentPage, totalPages]);

  const handleDownloadCsv = () => {
    const dateText = selectedDate || getTodayDateValue();
    const filename = `event-log-${dateText}.csv`;
    downloadCsv(filename, filteredLogs);
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [search, quickFilter, selectedDate]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  useEffect(() => {
    if (!showWatch) {
      if (
        quickFilter === "WATCH" ||
        quickFilter === "SOS" ||
        quickFilter === "HEART_RATE"
      ) {
        setQuickFilter("ALL");
      }
    }
    if (!ppeLicensed) {
      if (
        quickFilter === "HELMET" ||
        quickFilter === "VEST" ||
        quickFilter === "HARNESS"
      ) {
        setQuickFilter("ALL");
      }
    }
  }, [showWatch, ppeLicensed, quickFilter]);

  return (
    <div className="flex h-full min-h-0 flex-col gap-4 overflow-hidden">
      {canSeeAllEvents && (
        <section className="grid shrink-0 grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-4">
          <SummaryCard
            label="민트캠"
            value={summary.mintcam}
            maskIcon="/assets/icons/mintcam.png"
            iconColor="#EA580C"
          />

          {ppeLicensed && (
            <SummaryCard
              label="안전모 미착용"
              value={summary.helmet}
              maskIcon="/assets/icons/ppe_helmet.png"
              iconColor="#2563EB"
            />
          )}

          {ppeLicensed && (
            <SummaryCard
              label="안전조끼 미착용"
              value={summary.vest}
              maskIcon="/assets/icons/ppe_vest.png"
              iconColor="#8B5CF6"
            />
          )}

          {ppeLicensed && (
            <SummaryCard
              label="안전대 미착용"
              value={summary.harness}
              maskIcon="/assets/icons/ppe_harness.png"
              iconColor="#DC2626"
            />
          )}

          {showWatch && (
            <SummaryCard
              label="민트워치"
              value={summary.watch}
              accent="text-violet-600 dark:text-violet-300"
              icon="⌚"
            />
          )}

          {showWatch && (
            <SummaryCard
              label="SOS"
              value={summary.sos}
              accent="text-red-600 dark:text-red-300"
              icon="🆘"
            />
          )}

          {showWatch && (
            <SummaryCard
              label="심박수 이상"
              value={summary.heartRate}
              accent="text-sky-600 dark:text-sky-300"
              icon="❤️"
            />
          )}
        </section>
      )}

      <section className="flex min-h-0 flex-1 flex-col rounded-[28px] border border-white/70 bg-white/92 p-4 shadow-[0_16px_40px_rgba(15,23,42,0.06)] dark:border-white/10 dark:bg-[#111827]/82 sm:p-5">
        <div className="mb-4 shrink-0 flex flex-col gap-3">
          <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
            <div className="flex items-center gap-3">
              <div>
                <div className="text-lg font-bold text-gray-900 dark:text-white">
                  이벤트 목록
                </div>
                <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {loading
                    ? "불러오는 중..."
                    : `${
                        selectedDate ? `${selectedDate} 기준 ` : ""
                      }총 ${filteredLogs.length}건 · 업데이트 ${
                        lastUpdated
                          ? lastUpdated.toLocaleString("ko-KR", {
                              timeZone: "Asia/Seoul",
                              hour12: true,
                            })
                          : "-"
                      }`}
                </div>
              </div>

              <button
                type="button"
                onClick={() => {
                  fetchEvents({ silent: true });
                  fetchEventMarks();
                }}
                className="shrink-0 rounded-xl bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-700"
              >
                {refreshing ? "갱신 중..." : "새로고침"}
              </button>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <QuickFilterButton
                active={quickFilter === "ALL"}
                onClick={() => setQuickFilter("ALL")}
              >
                전체
              </QuickFilterButton>

              <QuickFilterButton
                active={quickFilter === "MINTCAM"}
                onClick={() => setQuickFilter("MINTCAM")}
              >
                민트캠
              </QuickFilterButton>

              {showWatch && (
                <QuickFilterButton
                  active={quickFilter === "WATCH"}
                  onClick={() => setQuickFilter("WATCH")}
                >
                  민트워치
                </QuickFilterButton>
              )}

              {ppeLicensed && (
                <>
                  <QuickFilterButton
                    active={quickFilter === "HELMET"}
                    onClick={() => setQuickFilter("HELMET")}
                  >
                    안전모
                  </QuickFilterButton>

                  <QuickFilterButton
                    active={quickFilter === "VEST"}
                    onClick={() => setQuickFilter("VEST")}
                  >
                    안전조끼
                  </QuickFilterButton>

                  <QuickFilterButton
                    active={quickFilter === "HARNESS"}
                    onClick={() => setQuickFilter("HARNESS")}
                  >
                    안전대
                  </QuickFilterButton>
                </>
              )}
            </div>
          </div>

          <div className="flex w-full min-w-0 flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
            <div className="flex w-full min-w-0 flex-col gap-3 sm:flex-row xl:w-auto">
              <EventCalendarInput
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                markedDateSet={eventDateSet}
              />

              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="검색어 입력 (장비명, ID, 내용, 비고)"
                className="h-12 w-full min-w-0 rounded-2xl border border-gray-200 bg-white px-4 text-[16px] outline-none focus:border-blue-400 dark:border-white/10 dark:bg-[#0F172A] dark:text-white sm:text-sm xl:w-[420px]"
              />
            </div>

            <button
              type="button"
              onClick={handleDownloadCsv}
              disabled={filteredLogs.length === 0}
              className={`h-12 w-full rounded-2xl px-4 text-sm font-semibold sm:w-auto ${
                filteredLogs.length === 0
                  ? "cursor-not-allowed bg-gray-200 text-gray-400 dark:bg-white/10 dark:text-gray-500"
                  : "bg-emerald-600 text-white hover:bg-emerald-700"
              }`}
            >
              CSV 내보내기
            </button>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-hidden">
          <EventTable logs={pagedLogs} onWatchClip={setClipEvent} />
          <EventMobileList logs={pagedLogs} onWatchClip={setClipEvent} />
        </div>

        <div className="shrink-0 pt-3">
          <EventPagination
            totalCount={filteredLogs.length}
            currentPage={safeCurrentPage}
            totalPages={totalPages}
            pageSize={PAGE_SIZE}
            pageNumbers={pageNumbers}
            onChangePage={setCurrentPage}
          />
        </div>
      </section>

      {clipEvent && (
        <ViolationClipModal
          event={clipEvent}
          onClose={() => setClipEvent(null)}
        />
      )}
    </div>
  );
}